import { useId, useState } from 'react';
import BaseCollapse from '@mui/material/Collapse';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import OwnersList, { OwnersListOwner } from '../../components/OwnersList/OwnersList';
import ViewTypeActionButton from '../../components/ViewTypeActionButton';
import SlaIndicator from '../../components/SlaIndicator/SlaIndicator';
import { cardSx, itemHeaderSx, itemSx, itemToggleButtonOpenSx, itemToggleButtonSx } from './styles';
import { ActiveProcessesItem, ActiveProcessesProps } from './types';
import { mergeSx } from 'framework/utils';

interface ActiveProcessesRowProps {
  item: ActiveProcessesItem;
  collapsible: boolean;
  renderOwner: (owner: OwnersListOwner) => React.ReactNode;
}

const ActiveProcessesRow = ({ item, collapsible, renderOwner }: ActiveProcessesRowProps) => {
  const id = useId();
  const detailsId = `${id}-details`;
  const toggleId = `${id}-toggle`;
  const [isExpanded, setIsExpanded] = useState(item.defaultExpanded ?? true);

  const header = (
    <Stack direction="row" sx={itemHeaderSx}>
      {collapsible ? (
        <IconButton
          id={toggleId}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.title} details`}
          aria-expanded={isExpanded}
          aria-controls={item.details?.length ? detailsId : undefined}
          onClick={() => setIsExpanded(prev => !prev)}
          sx={[itemToggleButtonSx, isExpanded && itemToggleButtonOpenSx]}
        />
      ) : null}

      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h6" color="brand">
            {item.title}
          </Typography>
          {item.slaEndDate ? <SlaIndicator endDate={item.slaEndDate} /> : null}
        </Stack>
        {item.description && (
          <Typography variant="body2" color="textSecondary">
            {item.description}
          </Typography>
        )}
      </Box>

      {item.owners?.length ? <OwnersList owners={item.owners} renderOwner={renderOwner} /> : null}

      <Stack direction="row" gap={2}>
        {item.action ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <ViewTypeActionButton action={item.action} defaultTextVariant="contained" />
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );

  const details = item.details?.length ? (
    <Stack gap={0.5} px={2}>
      {item.details.map(detail => (
        <Stack key={detail.label}>
          <Typography variant="caption" color="textSecondary">
            {detail.label}
          </Typography>

          {typeof detail.value === 'string' ? <Typography variant="caption">{detail.value}</Typography> : detail.value}
        </Stack>
      ))}
    </Stack>
  ) : null;

  if (!collapsible) {
    return (
      <Box sx={itemSx}>
        {header}
        {details}
      </Box>
    );
  }

  return (
    <Box sx={itemSx}>
      {header}
      <BaseCollapse
        id={detailsId}
        role={details ? 'region' : undefined}
        aria-labelledby={details ? toggleId : undefined}
        aria-hidden={!isExpanded}
        in={isExpanded}
      >
        {details}
      </BaseCollapse>
    </Box>
  );
};

const defaultRenderOwner = ({ id, name }: OwnersListOwner) => name ?? id;

const ActiveProcesses: React.FC<ActiveProcessesProps> = ({
  items,
  collapsible = true,
  renderOwner = defaultRenderOwner,
  sx,
}) => (
  <Box sx={mergeSx(cardSx, sx)}>
    <Stack gap={1}>
      {items.map(item => (
        <ActiveProcessesRow key={item.id} item={item} collapsible={collapsible} renderOwner={renderOwner} />
      ))}
    </Stack>
  </Box>
);

export default ActiveProcesses;
